import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../utils/apiClient';
import { MAX_SHOWCASED_ACHIEVEMENTS } from '../../data/achievements';

/**
 * Каталог достижений пользователя и витрина профиля.
 *
 * Значки вокруг аватара берутся не отсюда, а из `user.showcaseAchievements`,
 * которое приходит вместе с пользователем — этот хук нужен модалке достижений,
 * где витрину выбирают.
 *
 * @param {number|string} userId - чьи достижения смотрим
 * @param {boolean} [enabled] - грузить ли (модалка закрыта — запрос не нужен)
 */
export function useAchievements(userId, enabled = true) {
    const [achievements, setAchievements] = useState([]);
    const [itsme, setItsme] = useState(false);
    const [showcase, setShowcase] = useState([]);
    const [maxShowcase, setMaxShowcase] = useState(MAX_SHOWCASED_ACHIEVEMENTS);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!enabled || !userId) return;

        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);

            try {
                const url = new URL('/api/achievements', process.env.REACT_APP_API_URL);
                url.searchParams.append('userId', String(userId));

                const res = await apiFetch(url.toString(), { method: 'GET' });
                const data = await res.json();

                if (!res.ok || data?.error) throw new Error(data?.error ?? 'Ошибка сервера');
                if (cancelled) return;

                setAchievements(Array.isArray(data?.achievements) ? data.achievements : []);
                // В чужом профиле сервер отдаёт только полученные достижения,
                // поэтому «получено N из M» там не имеет смысла.
                setItsme(!!data?.itsme);
                setShowcase(Array.isArray(data?.showcase) ? data.showcase : []);
                setMaxShowcase(data?.maxShowcase ?? MAX_SHOWCASED_ACHIEVEMENTS);
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => { cancelled = true; };
    }, [userId, enabled]);

    const isSelected = useCallback((id) => showcase.includes(id), [showcase]);

    /**
     * Добавляет/убирает достижение из витрины.
     *
     * Сохраняем весь список целиком — он маленький, а раздельные add/remove
     * плодили бы гонки при быстром переключении галочек. Состояние правим
     * сразу, до ответа: галочка должна ставиться мгновенно.
     *
     * @param {number} id
     * @returns {Promise<Array<Object>|null>} новая витрина (развёрнутая) или
     *   null, если сохранить не удалось
     */
    const toggle = useCallback(async (id) => {
        if (saving) return null;

        const next = showcase.includes(id)
            ? showcase.filter(x => x !== id)
            : [...showcase, id];

        if (next.length > maxShowcase) return null;

        const previous = showcase;
        setShowcase(next);
        setSaving(true);

        try {
            const res = await apiFetch('/api/achievements/showcase', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids: next }),
            });

            if (!res.ok) throw new Error();

            // Профиль рисует значки из данных пользователя, а не отсюда, и
            // перезапрашивать его целиком ради витрины незачем — отдаём новый
            // список в том же виде, в каком его отдал бы сервер с профилем.
            return next
                .map(achievementId => achievements.find(a => a.id === achievementId))
                .filter(Boolean)
                .map(({ id: achievementId, code, title, image }) => ({ id: achievementId, code, title, image }));
        } catch {
            setShowcase(previous);
            return null;
        } finally {
            setSaving(false);
        }
    }, [achievements, showcase, maxShowcase, saving]);

    return {
        achievements,
        itsme,
        unlockedCount: achievements.filter(a => a.unlocked).length,
        total: achievements.length,
        showcase,
        showcaseCount: showcase.length,
        maxShowcase,
        isSelected,
        toggle,
        saving,
        loading,
        error,
    };
}
